import React, { useState, useEffect, useCallback } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Radio from 'components/commercetools-ui/atoms/radio';
import Skeleton from 'components/commercetools-ui/atoms/skeleton';
import { DeliveryProps } from 'components/commercetools-ui/organisms/checkout/types';
import useLocale from 'helpers/hooks/useLocale';
import useProcessing from 'helpers/hooks/useProcessing';
import { StringHelpers } from 'helpers/stringHelpers';
import { useCart } from 'frontastic';
import { CartDetails } from 'frontastic/hooks/useCart/types';
import { useAdyenCheckout } from 'components/commercetools-ui/organisms/checkout/provider';

export interface Props {
  goToNextStep: () => void;
  deliveryOptions: DeliveryProps[];
  onSelectDeliveryOption: (selectedId: string, resetData: boolean) => void;
  retainData: boolean;
}

const Delivery: React.FC<Props> = ({ goToNextStep, deliveryOptions, onSelectDeliveryOption, retainData = false }) => {
  const { getCountry } = useLocale();
  const [selectedId, setSelectedId] = useState(deliveryOptions?.[0]?.deliveryOptionId || '');
  const [retainDataCheckDone, setRetainDataCheckDone] = useState(false);
  const { getAvailableDeliveryOptions, setDeliveryOption, data, updateCart } = useCart();
  const { processing, startProcessing, stopProcessing } = useProcessing();
  const countryCode = getCountry();
  const [availableDeliveryOptions, setAvailableDeliveryOptions] = useState<DeliveryProps[]>([]);
  const { paymentStatus } = useAdyenCheckout();

  const resetAddressData = {
    country: countryCode,
  };

  const selectDeliveryOption = async (selectedId: string) => {
    setSelectedId(selectedId);
    onSelectDeliveryOption(selectedId, true);
  };

  useEffect(() => {
    if (!retainData) {
      setSelectedId(deliveryOptions?.[0]?.deliveryOptionId);
      onSelectDeliveryOption(deliveryOptions?.[0]?.deliveryOptionId, false);
    }
  }, []);

  useEffect(() => {
    if (retainData) {
      if (data?.custom?.deliveryOption && !retainDataCheckDone) {
        setRetainDataCheckDone(true);
        setSelectedId(data?.custom?.deliveryOption);
        onSelectDeliveryOption(data?.custom?.deliveryOption, false);
      }
    }
  }, [data]);

  useEffect(() => {
    const setSelectedDeliveryId = async () => {
      if (data?.custom?.deliveryOption) {
        setSelectedId(data?.custom?.deliveryOption);
      }
    };

    const fetchDeliveryOptions = async () => {
      try {
        const availableOptions = await getAvailableDeliveryOptions(countryCode);
        const availableOptionIds = availableOptions.map((opt) => opt.deliveryOptionId);
        const filteredOptions = deliveryOptions.filter((opt) => availableOptionIds.includes(opt.deliveryOptionId));

        setAvailableDeliveryOptions(filteredOptions);

        if (!filteredOptions.find((opt) => opt.deliveryOptionId === selectedId) && filteredOptions.length > 0) {
          setSelectedId(filteredOptions[0].deliveryOptionId); // Automatically select the first available delivery option
          if (retainData) {
            setSelectedDeliveryId();
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    if (countryCode && paymentStatus !== 'success' && data?.cartState == 'Active') {
      fetchDeliveryOptions();
    }
  }, [countryCode, deliveryOptions, getAvailableDeliveryOptions]);

  const submit = useCallback(async () => {
    if (!selectedId || processing) return;
    startProcessing();
    await setDeliveryOption(selectedId);
    const checkoutData = {
      shipping: resetAddressData,
      billing: resetAddressData,
    } as CartDetails;
    await updateCart(checkoutData);
    stopProcessing();
    goToNextStep();
  }, [goToNextStep, selectedId, processing, startProcessing, stopProcessing]);

  if (!availableDeliveryOptions || availableDeliveryOptions.length === 0) {
    return (
      <div className="md:max-w-[800px] lg:px-36 lg:pb-36 lg:pt-0">
        <div className="relative">
          <div className="relative mb-10 h-20 w-[180px]">
            <Skeleton></Skeleton>
          </div>
          <div className="relative h-48 w-full">
            <Skeleton></Skeleton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:px-36 lg:pb-36 lg:pt-0">
      <div className="mt-24 grid grid-cols-3 bg-white md:max-w-[800px] lg:mt-0">
        {availableDeliveryOptions.map((deliveryOption) => (
          <div
            key={deliveryOption.deliveryOptionId}
            className="col-span-3 flex cursor-pointer items-center justify-between p-16"
            onClick={() => selectDeliveryOption(deliveryOption.deliveryOptionId)}
          >
            <div className="flex items-start gap-16">
              <Radio name="checkout-shipping-method" checked={deliveryOption.deliveryOptionId === selectedId} />
              <div className="w-full">
                <p className="text-16 font-medium">{StringHelpers.titleCaseString(deliveryOption.deliveryOptionId)}</p>
                <p className="mt-4 text-14 font-light leading-5 text-secondary-black">
                  {deliveryOption.deliveryOptionDesc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-24">
        <Button
          variant="primary"
          className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36"
          loading={processing}
          type="submit"
          onClick={submit}
        >
          Continue to Addresses
        </Button>
      </div>
    </div>
  );
};

export default Delivery;
